/**
 * The conversational flow as the panel handles it: its pieces and the questions asked of
 * it.
 *
 * Kept apart from its editor and its preview because the three change for different
 * reasons, and because both the editor and whoever does the actual editing —
 * `flow-editing.js` — need these same pieces.
 */

import type { ConversationFlow, FlowButton, FlowMessage, FlowStep } from "../api/types";

/**
 * A bubble as the editor walks it. The entry batch's first one is the rule's private
 * message, whose text lives in the form: there it is `null`.
 */
export interface EditorMessage {
  text: string | null;
  buttons: FlowButton[];
  media?: FlowMessage["media"];
}

/** Dropdown value that creates a new step and hooks the button to it. */
export const NEW_STEP_OPTION = "__nuevo__";

export const MAX_BUTTONS_PER_MESSAGE = 3;

export const MAX_FLOW_STEPS = 10;

export const MAX_BUTTON_LABEL_LENGTH = 20;

export const MAX_MESSAGES_PER_STEP = 4;

export function emptyFlow(): ConversationFlow {
  return { buttons: [], messages: [], steps: [] };
}

export function newFlowButton(): FlowButton {
  return { label: "", kind: "reply", url: "", nextStepId: "" };
}

export function newFlowMessage(): FlowMessage {
  return { text: "", buttons: [], media: null };
}

export function newFlowStep(flow: ConversationFlow): FlowStep {
  return { id: newStepId(flow), messages: [newFlowMessage()] };
}

/** A free identifier in the shape the server expects. */
export function newStepId(flow: ConversationFlow): string {
  const used = new Set(flow.steps.map((step) => step.id));
  for (let index = 1; ; index++) {
    const candidate = `paso-${index}`;
    if (!used.has(candidate)) return candidate;
  }
}

/**
 * The bubbles of a batch. `-1` is the entry one, and its first bubble is the rule's private
 * message: its text lives in the form and not in the flow, so here it only contributes the
 * buttons.
 */
export function messagesOfStep(flow: ConversationFlow, stepIndex: number): EditorMessage[] {
  if (stepIndex >= 0) return flow.steps[stepIndex]?.messages ?? [];
  return [{ text: null, buttons: flow.buttons }, ...flow.messages];
}

/** The specific bubble `(batch, position)`, with the same button array as the flow. */
export function messageAt(flow: ConversationFlow, stepIndex: number, messageIndex: number): EditorMessage | null {
  return messagesOfStep(flow, stepIndex)[messageIndex] ?? null;
}

/**
 * Can this bubble still take buttons?
 *
 * A bubble carrying a file cannot: Instagram's message is either a text or one attachment,
 * and the button template is itself an attachment. The editor hides the option rather than
 * offering it and failing on save.
 */
export function carriesMedia(message: EditorMessage | null | undefined): boolean {
  return Boolean(message?.media?.url);
}

/** Every button in the flow, to walk it without repeating the trip through the batches. */
export function allFlowButtons(flow: ConversationFlow): FlowButton[] {
  return [
    ...flow.buttons,
    ...flow.messages.flatMap((message) => message.buttons ?? []),
    ...flow.steps.flatMap((step) => (step.messages ?? []).flatMap((message) => message.buttons ?? [])),
  ];
}

/**
 * Does the follow gate stand in front of the first message?
 *
 * Mirror of the rule the domain applies: the gate stands wherever the automation stops
 * inviting and starts delivering. With a reply button in the entry batch, that is behind
 * the button; with nothing hanging off it, the first message is the whole promise and the
 * gate goes in front of it.
 */
export function gateStandsAtEntry(flow: ConversationFlow): boolean {
  const entryButtons = [flow.buttons, ...flow.messages.map((message) => message.buttons ?? [])].flat();
  return !entryButtons.some((button) => button.kind === "reply");
}

/** Steps reached by following buttons from the entry batch. */
export function reachableSteps(flow: ConversationFlow): Set<string> {
  const reachable = new Set<string>();

  const exitsOf = (step: FlowStep | null | undefined): string[] => {
    const messages = step ? (step.messages ?? []) : messagesOfStep(flow, -1);
    return messages
      .flatMap((message) => message.buttons ?? [])
      .filter((button) => button.kind === "reply" && button.nextStepId)
      .map((button) => button.nextStepId);
  };

  const pending = exitsOf(null);
  while (pending.length > 0) {
    const stepId = pending.pop()!;
    if (reachable.has(stepId)) continue;
    reachable.add(stepId);
    const step = flow.steps.find((candidate) => candidate.id === stepId);
    if (step) pending.push(...exitsOf(step));
  }

  return reachable;
}
